import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Send, Image as ImageIcon, OpenInNew, X } from '@mui/icons-material';
import chatAPI, {
  ChatAttachment,
  ChatConversation,
  ChatMessage,
  resolveChatMediaUrl,
} from '../services/chatApi';
import useChatSocket from '../hooks/useChatSocket';
import { getImageFileFromClipboard } from '../utils/chatClipboardImage';

const STATUS_LABELS: Record<string, string> = {
  open: 'Ouverte',
  closed: 'Fermée',
  pending: 'En attente',
  'not-started': 'Non ouverte',
};

const STATUS_COLORS: Record<string, 'success' | 'default' | 'warning' | 'info'> = {
  open: 'success',
  closed: 'default',
  pending: 'warning',
  'not-started': 'info',
};

const ITEM_TYPE_LABELS: Record<string, string> = {
  capsule: 'Capsule',
  quiz: 'Quiz',
  workshop: 'Atelier',
  assessment: 'Diagnostic / Impact',
};

const formatDayLabel = (dateStr: string): string => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === yesterday.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatTime = (dateStr: string) =>
  new Date(dateStr).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

const formatReadAt = (dateStr: string) => {
  const d = new Date(dateStr);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return formatTime(dateStr);
  }
  return d.toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStudentReadLabel = (msg: ChatMessage): string | null => {
  if (msg.senderRole !== 'admin') return null;
  if (!msg.readByStudent) return 'Non lu';
  if (msg.readByStudentAt) return `Vu · ${formatReadAt(msg.readByStudentAt)}`;
  return 'Vu';
};

const getStudent = (conv: ChatConversation) => {
  if (typeof conv.student === 'object' && conv.student) return conv.student;
  return null;
};

const getLocalizedText = (obj?: Record<string, string>, fallback = ''): string => {
  if (!obj) return fallback;
  return obj.fr || obj.en || Object.values(obj)[0] || fallback;
};

const getCourseLabel = (conv: ChatConversation): string =>
  conv.context?.courseProject?.trim() || '';

const requestNotificationPermission = async () => {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'default') await Notification.requestPermission();
};

const showBrowserNotification = (title: string, body: string) => {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  if (!document.hidden) return;
  try {
    new Notification(title, { body });
  } catch {
    // ignore
  }
};

const getStudentName = (conv: ChatConversation) => {
  const s = getStudent(conv);
  return s ? `${s.firstName} ${s.lastName}`.trim() : 'Apprenant';
};

const hasPopulatedStudent = (
  student: ChatConversation['student']
): student is { _id: string; firstName: string; lastName: string; email?: string } =>
  typeof student === 'object' &&
  student !== null &&
  'firstName' in student &&
  Boolean((student as { firstName?: string }).firstName);

/** Socket/API partial updates often carry student as a raw id — keep enriched fields. */
const mergeConversationUpdate = (
  existing: ChatConversation,
  incoming: Partial<ChatConversation>
): ChatConversation => {
  const merged: ChatConversation = { ...existing, ...incoming };
  if (
    incoming.student === undefined ||
    !hasPopulatedStudent(incoming.student)
  ) {
    merged.student = existing.student;
  }
  if (!incoming.primaryCourseId && existing.primaryCourseId) {
    merged.primaryCourseId = existing.primaryCourseId;
  }
  if (incoming.context || existing.context) {
    merged.context = {
      ...existing.context,
      ...incoming.context,
      courseProject:
        incoming.context?.courseProject?.trim() ||
        existing.context?.courseProject?.trim() ||
        incoming.context?.courseProject ||
        existing.context?.courseProject,
      item: incoming.context?.item ?? existing.context?.item,
    };
  }
  return merged;
};

const StudentChatInbox: React.FC = () => {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [activeConversation, setActiveConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const list = await chatAPI.listConversations({
        status: statusFilter === 'all' ? undefined : statusFilter,
        unreadOnly,
      });
      setConversations(list);
    } catch {
      setError('Impossible de charger les conversations.');
    } finally {
      setLoadingList(false);
    }
  }, [statusFilter, unreadOnly]);

  const loadThread = useCallback(async (id: string) => {
    setLoadingThread(true);
    setError(null);
    try {
      const data = await chatAPI.getConversation(id);
      setActiveConversation(data.conversation);
      setMessages(data.messages);
      await chatAPI.markRead(id);
      setConversations((prev) =>
        prev.map((c) => (c._id === id ? { ...c, unreadByAdmin: 0 } : c))
      );
    } catch {
      setError('Impossible de charger la conversation.');
    } finally {
      setLoadingThread(false);
    }
  }, []);

  useEffect(() => {
    loadList();
    requestNotificationPermission();
  }, [loadList]);

  useEffect(() => {
    if (conversationId) loadThread(conversationId);
    else {
      setActiveConversation(null);
      setMessages([]);
    }
  }, [conversationId, loadThread]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
  }, []);

  useEffect(() => {
    if (loadingThread || !conversationId) return;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToBottom('auto'));
    });
    return () => cancelAnimationFrame(frame);
  }, [messages, typing, loadingThread, conversationId, scrollToBottom]);

  const handleIncomingMessage = useCallback(
    (payload: { conversationId: string; message: ChatMessage }) => {
      const msg = payload.message;
      loadList();

      if (conversationId === payload.conversationId) {
        setMessages((prev) => {
          if (prev.some((m) => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
        if (msg.senderRole === 'student') {
          chatAPI.markRead(payload.conversationId).catch(() => undefined);
        }
      } else if (msg.senderRole === 'student') {
        showBrowserNotification(
          'Nouveau message apprenant',
          msg.content || 'Image'
        );
      }
    },
    [conversationId, loadList]
  );

  const handleConversationUpdated = useCallback(
    (payload: { conversation: ChatConversation }) => {
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c._id === payload.conversation._id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = mergeConversationUpdate(next[idx], payload.conversation);
          return next.sort(
            (a, b) =>
              new Date(b.lastMessageAt || 0).getTime() -
              new Date(a.lastMessageAt || 0).getTime()
          );
        }
        return [payload.conversation, ...prev];
      });
      if (conversationId === payload.conversation._id) {
        setActiveConversation((prev) =>
          prev ? mergeConversationUpdate(prev, payload.conversation) : payload.conversation
        );
      }
    },
    [conversationId]
  );

  const handleTyping = useCallback(
    (payload: { conversationId: string; role: string; isTyping: boolean }) => {
      if (conversationId !== payload.conversationId) return;
      if (payload.role === 'student') {
        if (payload.isTyping) {
          setTyping(true);
          if (typingClearRef.current) clearTimeout(typingClearRef.current);
          typingClearRef.current = setTimeout(() => setTyping(false), 3000);
        } else setTyping(false);
      }
    },
    [conversationId]
  );

  const handleMessagesRead = useCallback(
    (payload: { conversationId: string; readerRole: 'student' | 'admin'; readAt: string }) => {
      if (payload.readerRole !== 'student') return;
      if (conversationId !== payload.conversationId) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.senderRole === 'admin' && !m.readByStudent
            ? { ...m, readByStudent: true, readByStudentAt: payload.readAt }
            : m
        )
      );
    },
    [conversationId]
  );

  const { emitTyping, joinConversation, leaveConversation } = useChatSocket(
    token,
    {
      onMessage: handleIncomingMessage as any,
      onConversationUpdated: handleConversationUpdated as any,
      onTyping: handleTyping,
      onMessagesRead: handleMessagesRead,
    },
    Boolean(token)
  );

  useEffect(() => {
    if (!conversationId) return;
    joinConversation(conversationId);
    return () => leaveConversation(conversationId);
  }, [conversationId, joinConversation, leaveConversation]);

  const handleDraftChange = (value: string) => {
    setDraft(value);
    if (!conversationId) return;
    emitTyping(conversationId, true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => emitTyping(conversationId, false), 1200);
  };

  const uploadImageFile = async (file: File) => {
    if (!file.type.startsWith('image/') || attachments.length >= 4) return;
    setUploading(true);
    try {
      const att = await chatAPI.uploadImage(file);
      setAttachments((prev) => [...prev, att]);
    } catch {
      setError("Impossible d'envoyer l'image.");
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadImageFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    if (uploading || attachments.length >= 4) return;
    const file = getImageFileFromClipboard(e);
    if (!file) return;
    e.preventDefault();
    await uploadImageFile(file);
  };

  const handleSend = async () => {
    if (!conversationId || (!draft.trim() && attachments.length === 0) || sending) return;
    setSending(true);
    setError(null);
    try {
      const result = await chatAPI.sendMessage(conversationId, draft.trim(), attachments);
      setDraft('');
      setAttachments([]);
      setMessages((prev) => {
        if (prev.some((m) => m._id === result.message._id)) return prev;
        return [...prev, result.message];
      });
      setActiveConversation((prev) =>
        prev ? mergeConversationUpdate(prev, result.conversation) : result.conversation
      );
      emitTyping(conversationId, false);
      loadList();
    } catch {
      setError("Impossible d'envoyer le message.");
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status: ChatConversation['status']) => {
    if (!conversationId) return;
    try {
      const updated = await chatAPI.updateStatus(conversationId, status);
      setActiveConversation((prev) =>
        prev ? mergeConversationUpdate(prev, updated) : updated
      );
      loadList();
    } catch {
      setError('Impossible de mettre à jour le statut.');
    }
  };

  const groupedMessages = useMemo(() => {
    const groups: { day: string; items: ChatMessage[] }[] = [];
    messages.forEach((msg) => {
      const day = formatDayLabel(msg.createdAt);
      const last = groups[groups.length - 1];
      if (last?.day === day) last.items.push(msg);
      else groups.push({ day, items: [msg] });
    });
    return groups;
  }, [messages]);

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unreadByAdmin || 0), 0);

  const studentProfileLink = activeConversation
    ? (() => {
        const s = getStudent(activeConversation);
        const courseId =
          activeConversation.primaryCourseId ||
          activeConversation.context?.courseId ||
          activeConversation.course;
        if (s && courseId) {
          return `/courses/${courseId}/students/${s._id}/detail`;
        }
        return null;
      })()
    : null;

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', gap: 0, bgcolor: '#fff', borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
      {/* Inbox list */}
      <Box sx={{ width: 340, borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
          <Typography variant="h6" fontWeight={700}>
            Chat apprenants
            {totalUnread > 0 && (
              <Chip label={totalUnread} size="small" color="error" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="open">Ouvertes</MenuItem>
                <MenuItem value="pending">En attente</MenuItem>
                <MenuItem value="closed">Fermées</MenuItem>
                <MenuItem value="not-started">Non ouvertes</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              variant={unreadOnly ? 'contained' : 'outlined'}
              onClick={() => setUnreadOnly((v) => !v)}
            >
              Non lus
            </Button>
          </Box>
        </Box>

        <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          {loadingList ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : conversations.length === 0 ? (
            <Typography sx={{ p: 3, color: 'text.secondary', textAlign: 'center' }}>
              Aucune conversation
            </Typography>
          ) : (
            conversations.map((conv) => {
              const student = getStudent(conv);
              const name = student ? `${student.firstName} ${student.lastName}` : 'Apprenant';
              const isActive = conv._id === conversationId;
              return (
                <ListItemButton
                  key={conv._id}
                  selected={isActive}
                  onClick={() => navigate(`/chat/${conv._id}`)}
                  sx={{ borderBottom: '1px solid #f0f0f0', py: 1.5 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={conv.unreadByAdmin ? 700 : 500} noWrap>
                          {name}
                        </Typography>
                        {conv.unreadByAdmin > 0 && (
                          <Badge badgeContent={conv.unreadByAdmin} color="error" />
                        )}
                        <Chip
                          label={STATUS_LABELS[conv.status] || conv.status}
                          size="small"
                          color={STATUS_COLORS[conv.status] || 'default'}
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        {getCourseLabel(conv) && (
                          <Typography variant="caption" color="primary" display="block" noWrap>
                            {getCourseLabel(conv)}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {conv.lastMessagePreview || '—'}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })
          )}
        </List>
      </Box>

      {/* Thread */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {!conversationId ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">
              Sélectionnez une conversation pour répondre
            </Typography>
          </Box>
        ) : loadingThread ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  {activeConversation ? getStudentName(activeConversation) : ''}
                </Typography>
                {activeConversation && getStudent(activeConversation)?.email && (
                  <Typography variant="body2" color="text.secondary">
                    {getStudent(activeConversation)?.email}
                  </Typography>
                )}
                {activeConversation?.context && (
                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {getCourseLabel(activeConversation) && (
                      <Chip
                        size="small"
                        label={getCourseLabel(activeConversation)}
                        variant="outlined"
                        color="primary"
                      />
                    )}
                    {activeConversation.context.item?.itemType && (
                      <Chip
                        size="small"
                        label={`${ITEM_TYPE_LABELS[activeConversation.context.item.itemType] || activeConversation.context.item.itemType}${
                          activeConversation.context.item.itemTitle
                            ? ` — ${getLocalizedText(activeConversation.context.item.itemTitle)}`
                            : ''
                        }`}
                        variant="outlined"
                      />
                    )}
                    {activeConversation.context.item?.path && (
                      <Chip size="small" label={activeConversation.context.item.path} variant="outlined" />
                    )}
                  </Box>
                )}
              </Box>
              {studentProfileLink && (
                <Button
                  component={Link}
                  to={studentProfileLink}
                  size="small"
                  startIcon={<OpenInNew />}
                  variant="outlined"
                >
                  Fiche apprenant
                </Button>
              )}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={activeConversation?.status || 'open'}
                  label="Statut"
                  onChange={(e) => handleStatusChange(e.target.value as ChatConversation['status'])}
                >
                  <MenuItem value="open">Ouverte</MenuItem>
                  <MenuItem value="pending">En attente</MenuItem>
                  <MenuItem value="closed">Fermée</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box ref={messagesContainerRef} sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f7f7f8' }}>
              {groupedMessages.map((group) => (
                <Box key={group.day}>
                  <Typography
                    align="center"
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', my: 1 }}
                  >
                    {group.day}
                  </Typography>
                  {group.items.map((msg) => {
                    const isStudent = msg.senderRole === 'student';
                    const isSystem = msg.senderRole === 'system';
                    const studentReadLabel = getStudentReadLabel(msg);
                    return (
                      <Box
                        key={msg._id}
                        sx={{
                          display: 'flex',
                          justifyContent: isSystem ? 'center' : isStudent ? 'flex-start' : 'flex-end',
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '75%',
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: isSystem
                              ? 'transparent'
                              : isStudent
                              ? '#fff'
                              : '#2eab80',
                            color: isSystem ? 'text.secondary' : isStudent ? 'text.primary' : '#fff',
                            border: isStudent ? '1px solid #e8e8e8' : 'none',
                            fontSize: isSystem ? '0.85rem' : 'inherit',
                            textAlign: isSystem ? 'center' : 'left',
                          }}
                        >
                          {msg.content && (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {msg.content}
                            </Typography>
                          )}
                          {msg.attachments?.map((att, i) => (
                            <Box key={i} component="a" href={resolveChatMediaUrl(att.url)} target="_blank" rel="noopener noreferrer">
                              <Box
                                component="img"
                                src={resolveChatMediaUrl(att.url)}
                                alt={att.filename}
                                sx={{ maxWidth: '100%', borderRadius: 1, mt: 0.5, display: 'block' }}
                              />
                            </Box>
                          ))}
                          {!isSystem && (
                            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.5, textAlign: 'right' }}>
                              {formatTime(msg.createdAt)}
                              {studentReadLabel && (
                                <>
                                  <br />
                                  <Box
                                    component="span"
                                    sx={{
                                      fontStyle: studentReadLabel === 'Non lu' ? 'italic' : 'normal',
                                      opacity: studentReadLabel === 'Non lu' ? 0.85 : 1,
                                    }}
                                  >
                                    {studentReadLabel}
                                  </Box>
                                </>
                              )}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))}
              {typing && (
                <Typography variant="caption" color="text.secondary" fontStyle="italic">
                  L'apprenant est en train d'écrire…
                </Typography>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {attachments.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, p: 1, borderTop: '1px solid #eee' }}>
                {attachments.map((att, i) => (
                  <Box key={i} sx={{ position: 'relative', width: 56, height: 56 }}>
                    <Box
                      component="img"
                      src={resolveChatMediaUrl(att.url)}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }}
                    />
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', color: '#293330' }}
                      onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                    >
                      <X fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {error && (
              <Typography color="error" variant="body2" sx={{ px: 2 }}>
                {error}
              </Typography>
            )}

            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 2 }}>
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleImageSelect} />
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || attachments.length >= 4}
              >
                {uploading ? <CircularProgress size={20} /> : <ImageIcon />}
              </IconButton>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                size="small"
                placeholder="Répondre à l'apprenant…"
                value={draft}
                onChange={(e) => handleDraftChange(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <IconButton color="primary" onClick={handleSend} disabled={sending || (!draft.trim() && attachments.length === 0)}>
                {sending ? <CircularProgress size={20} /> : <Send />}
              </IconButton>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default StudentChatInbox;
